
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const Experience = () => {
  const { elementRef, isVisible } = useScrollAnimation();

  const experiences = [
    {
      period: 'November 2019 - December 2024',
      title: 'Administrative Secretary',
      company: 'Pietermaritzburg Muslim Women\'s Organisation',
      location: 'Pietermaritzburg',
      responsibilities: [
        'Managed bookkeeping, financial records, and internal databases to ensure accuracy',
        'Organized and scheduled meetings, prepared agendas, and maintained office filing systems',
        'Coordinated and executed various projects and programs, ensuring smooth operations',
        'Served as staff and facilitated communication between different departments',
        'Acted as the first point of contact for visitors and clients, providing professional administrative support'
      ],
      icon: 'ri-building-line',
      color: 'blue'
    },
    {
      period: 'January 2023 - July 2023',
      title: 'Agricultural Assistant Practitioner',
      company: 'Department of Agriculture',
      location: 'KwaZulu-Natal',
      responsibilities: [
        'Provided technical support and consultation for various agricultural projects',
        'Assisted in planning and implementing sustainable farming practices',
        'Conducted field assessments and provided recommendations for improving crop yields',
        'Monitored project progress and ensured compliance with agricultural standards',
        'Helped organize training workshops and meetings for farmers and agricultural workers',
        'Collaborated with supervisors to monitor project progress and ensure compliance with regulations'
      ],
      icon: 'ri-plant-line',
      color: 'green'
    },
    {
      period: 'February 2020 - November 2022',
      title: 'Agricultural Practitioner',
      company: 'Blind and Deaf Society, Hydroponic Farming',
      location: 'KwaZulu-Natal',
      responsibilities: [
        'Managed the cultivation, irrigation, and fertilization of crops to maximize yield',
        'Implemented integrated pest management strategies to control and prevent crop diseases',
        'Conducted soil testing and nutrient analysis to improve soil health',
        'Provided training and guidance on best practices in crop rotation, pruning, and sustainable farming',
        'Developed and maintained efficient irrigation systems to optimize water usage and reduce waste'
      ],
      icon: 'ri-seedling-line',
      color: 'amber'
    }
  ];

  return (
    <section id="experience" className="py-20 bg-gradient-to-br from-blue-50 to-green-50 relative overflow-hidden">
      <div className="absolute inset-0 bg-pattern opacity-20"></div>
      
      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div ref={elementRef} className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 gradient-earth font-serif">Professional Experience</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-green-600 mx-auto rounded-full"></div>
          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto">
            Over 5 years of diverse experience in agricultural practices and administrative management
          </p>
        </div>

        {/* Experience Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-400 to-green-400 rounded-full"></div>

          {experiences.map((exp, index) => (
            <div 
              key={index}
              className={`relative mb-16 transition-all duration-1200 ${isVisible ? 'animate-fade-in-up' : 'opacity-0 translate-y-8'}`}
              style={{ animationDelay: `${index * 0.3}s` }}
            >
              {/* Timeline Dot */}
              <div className={`absolute left-6 md:left-1/2 transform md:-translate-x-1/2 w-6 h-6 bg-gradient-to-br from-${exp.color}-400 to-${exp.color}-600 rounded-full border-4 border-white shadow-lg z-10`}>
                <div className={`absolute inset-1 bg-${exp.color}-300 rounded-full animate-pulse`}></div>
              </div>

              {/* Content Card */}
              <div className={`ml-20 md:ml-0 ${index % 2 === 0 ? 'md:mr-1/2 md:pr-12' : 'md:ml-1/2 md:pl-12'}`}>
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl hover-lift card-hover">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 bg-gradient-to-br from-${exp.color}-400 to-${exp.color}-600 rounded-xl flex items-center justify-center text-white text-xl`}>
                        <i className={exp.icon}></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-800 font-serif">{exp.title}</h3>
                        <p className="text-lg text-gray-600 font-medium">{exp.company}</p>
                        <p className="text-sm text-gray-500">{exp.location}</p>
                      </div>
                    </div>
                    <div className={`bg-gradient-to-r from-${exp.color}-100 to-${exp.color}-200 px-4 py-2 rounded-full`}>
                      <span className={`text-${exp.color}-800 text-sm font-semibold whitespace-nowrap`}>{exp.period}</span>
                    </div>
                  </div>

                  {/* Responsibilities */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">Key Responsibilities:</h4>
                    <ul className="space-y-3">
                      {exp.responsibilities.map((responsibility, respIndex) => (
                        <li key={respIndex} className="flex items-start space-x-3">
                          <div className={`w-2 h-2 bg-${exp.color}-500 rounded-full mt-2 flex-shrink-0`}></div>
                          <span className="text-gray-700 leading-relaxed">{responsibility}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
